import React, { useState } from 'react';
import './WhatsNew.css';

const FEATURES = [
  {
    version: 'Alert Center',
    date: 'May 2026',
    tag: 'major',
    dot: '🔔',
    cards: [
      {
        emoji: '🔔',
        title: 'Bell icon — top right of every admin page',
        desc: 'A bell icon sits fixed at the top-right corner on every admin page. When something needs your attention, it blinks and shakes. A rose badge shows how many unread alerts you have.',
        type: 'admin',
      },
      {
        emoji: '✨',
        title: 'What triggers an alert',
        desc: 'Four things create an alert: a new customer booking, a new contact form message, a customer rescheduling their own appointment, and a customer cancelling their own appointment.',
        type: 'auto',
      },
      {
        emoji: '🔊',
        title: 'Sound chime',
        desc: 'A soft two-tone chime plays when a new alert arrives. Click the bell once to unlock audio — after that, every new alert chimes automatically.',
        type: 'auto',
      },
      {
        emoji: '🗑️',
        title: 'Managing alerts',
        desc: 'Click the bell to open the tray. Click any alert row to navigate directly to the relevant appointment or message — the alert is cleared automatically. Use the × button to dismiss one, or Clear all to wipe everything.',
        type: 'admin',
      },
      {
        emoji: '💾',
        title: 'Missed alerts are saved',
        desc: 'Alerts are stored in the database. If you were offline or logged out when something happened, you\'ll see it the next time you open the admin panel. Alerts auto-delete after 7 days.',
        type: 'auto',
      },
    ],
  },
  {
    version: 'Booking Wizard',
    date: 'May 2026',
    tag: 'major',
    dot: '✦',
    cards: [
      {
        emoji: '1️⃣',
        title: 'Step 1 — Customer picks a service',
        desc: 'The public booking page shows three clickable service cards: Haircut (1 hr), Hair Coloring (3 hr), Haircut + Coloring (4 hr). Customer taps one and hits Continue.',
        type: 'customer',
      },
      {
        emoji: '2️⃣',
        title: 'Step 2 — Customer picks a date and time',
        desc: 'Date pills show the next 15 weekdays. Time slots show only the times that actually fit the chosen service — a 3-hour coloring won\'t show a slot that would run past closing. Customer picks both and hits Continue.',
        type: 'customer',
      },
      {
        emoji: '3️⃣',
        title: 'Step 3 — Customer fills in their details',
        desc: 'First name, last name, phone, email (confirmed twice), optional note, and consent checkbox. Customer hits Reserve my chair.',
        type: 'customer',
      },
      {
        emoji: '📧',
        title: 'Confirmation emails sent automatically',
        desc: 'The customer receives a confirmation email with their appointment details and a link to manage their booking. You receive an owner notification email at the same time.',
        type: 'auto',
      },
      {
        emoji: '🔔',
        title: 'Alert fires immediately',
        desc: 'The moment a booking is confirmed, the admin bell blinks with a "New booking" alert showing the customer name, service, date and time.',
        type: 'auto',
      },
    ],
  },
  {
    version: 'Walk-in Form',
    date: 'May 2026',
    tag: 'minor',
    dot: '✦',
    cards: [
      {
        emoji: '🚶',
        title: 'Creating a walk-in appointment',
        desc: 'Go to Walk-in in the sidebar. Pick a service first — date and time slots appear after. Fill in the customer\'s name, phone, and email, then hit Book walk-in.',
        type: 'admin',
      },
      {
        emoji: '📧',
        title: 'Customer gets a confirmation email',
        desc: 'Walk-in bookings send the same confirmation email as online bookings — the customer gets their appointment details and a manage link.',
        type: 'auto',
      },
      {
        emoji: '⏳',
        title: 'Slots update by service',
        desc: 'When you change the service, available times re-fetch automatically. Only slots that fit the full service duration are shown.',
        type: 'admin',
      },
    ],
  },
  {
    version: 'Operations Dashboard',
    date: 'May 2026',
    tag: 'major',
    dot: '✦',
    cards: [
      {
        emoji: '📊',
        title: 'Summary tab',
        desc: 'The default view. Shows count cards for every status, today\'s full schedule, and the latest bookings in the last 48 hours. Click any booking in Latest bookings to open its drawer.',
        type: 'admin',
      },
      {
        emoji: '📋',
        title: 'Status tabs — what each one means',
        desc: 'Upcoming: booked, not yet started. Working: currently in the chair. Completed: done. Missing: appointment time passed, customer didn\'t show. Cancelled: customer or admin cancelled.',
        type: 'admin',
      },
      {
        emoji: '👆',
        title: 'Opening an appointment drawer',
        desc: 'Click any row in any tab to open the appointment drawer. You\'ll see full details — service, date, time, customer contact, reschedule history, and internal notes.',
        type: 'admin',
      },
      {
        emoji: '⚡',
        title: 'Actions per status',
        desc: 'Upcoming: Push to working, Reschedule, Mark missing, Cancel, Trash. Working: Push to upcoming, Mark completed. Completed: Push to working, Trash. Missing: Push to upcoming, Reschedule, Trash. Cancelled: Push to upcoming, Reschedule, Trash.',
        type: 'admin',
      },
      {
        emoji: '📝',
        title: 'Internal notes',
        desc: 'Each appointment has an owner notes field in the drawer. Type anything — it saves automatically when you click away. Only visible to you, never shown to the customer.',
        type: 'admin',
      },
      {
        emoji: '⚡',
        title: 'Tab switching is instant',
        desc: 'All tabs stay loaded in the background. Switching between Upcoming, Working, and other tabs is instant — no re-fetch, no loading spinner. Search, sort, and page position are preserved per tab.',
        type: 'admin',
      },
    ],
  },
  {
    version: 'Service Catalog',
    date: 'May 2026',
    tag: 'major',
    dot: '✦',
    cards: [
      {
        emoji: '✂️',
        title: 'Three services',
        desc: 'Haircut — 1 hour. Hair Coloring — 3 hours. Haircut + Coloring — 4 hours. Every booking stores the duration at the time of booking, so historical records are always accurate.',
        type: 'admin',
      },
      {
        emoji: '🕑',
        title: '2pm last start cutoff',
        desc: 'No new appointments can start after 2:00 PM. A 4-hour Haircut + Coloring starting at 2pm runs until 6pm — that\'s fine. But nothing can start at 2:30pm or later.',
        type: 'auto',
      },
      {
        emoji: '🗓️',
        title: 'Smart slot blocking',
        desc: 'A 3-hour coloring starting at 11am blocks 11:00, 11:30, 12:00, 12:30, 13:00, and 13:30. No other appointment can start during any of those slots. Customers only see times that actually fit.',
        type: 'auto',
      },
    ],
  },
  {
    version: 'Customer Self-Service',
    date: 'May 2026',
    tag: 'minor',
    dot: '✦',
    cards: [
      {
        emoji: '🔗',
        title: 'Manage link in confirmation email',
        desc: 'Every confirmation email contains a "Manage your booking" link. Customers can click it anytime to view their appointment, reschedule, or cancel — no account needed.',
        type: 'customer',
      },
      {
        emoji: '↻',
        title: 'Customer reschedule',
        desc: 'Customer picks a new date and time. They get a reschedule confirmation email. You get an owner notification email. The alert bell blinks with a "Rescheduled" alert.',
        type: 'customer',
      },
      {
        emoji: '✕',
        title: 'Customer cancel',
        desc: 'Customer cancels their appointment. They get a cancellation confirmation email. You get an owner notification email. The alert bell blinks with a "Cancelled" alert.',
        type: 'customer',
      },
      {
        emoji: '🔒',
        title: '24-hour cutoff',
        desc: 'Customers cannot reschedule or cancel within 24 hours of their appointment. They see a message to contact the salon directly. You handle it from the admin drawer.',
        type: 'auto',
      },
    ],
  },
  {
    version: 'Contact & Messages',
    date: 'May 2026',
    tag: 'minor',
    dot: '✦',
    cards: [
      {
        emoji: '✉️',
        title: 'Customer sends a message',
        desc: 'The public website has a contact form. Customers fill in their name, email, phone, subject, and message and submit. You get an owner notification email and the alert bell blinks.',
        type: 'customer',
      },
      {
        emoji: '📥',
        title: 'Messages tab in admin',
        desc: 'Go to Messages in the sidebar. You\'ll see All, Unreplied, and Replied tabs. Click any message to open it and read the full content.',
        type: 'admin',
      },
      {
        emoji: '↩️',
        title: 'Replying to a customer',
        desc: 'Inside a message, type your reply and hit Send. The customer receives a branded reply email from Bella 7. The message moves to the Replied tab automatically.',
        type: 'admin',
      },
      {
        emoji: '🔔',
        title: 'Alert fires on new message',
        desc: 'Every new contact form submission creates an alert — "New message from [Name] — [Subject]". Clicking it takes you directly to the Messages tab.',
        type: 'auto',
      },
    ],
  },
  {
    version: 'Schedule Blocks',
    date: 'May 2026',
    tag: 'minor',
    dot: '✦',
    cards: [
      {
        emoji: '📅',
        title: 'Blocking a day or time range',
        desc: 'Go to Schedule in the sidebar. Click any day on the calendar. Choose Full day to block the entire day, or Time range to block specific hours. Pick a reason (vacation, holiday, sick, etc.) and save.',
        type: 'admin',
      },
      {
        emoji: '⛔',
        title: 'Customers can\'t book blocked times',
        desc: 'Blocked days and time ranges are immediately unavailable on the public booking form. Customers won\'t see those slots at all.',
        type: 'auto',
      },
      {
        emoji: '📧',
        title: 'Existing appointments auto-cancelled',
        desc: 'When you create a block, the system checks for existing appointments in that window. You\'ll see a list of affected appointments. If you check "Cancel and email customers", they all get a cancellation email automatically.',
        type: 'auto',
      },
      {
        emoji: '📋',
        title: 'Upcoming blocks panel',
        desc: 'The right side of the Schedule page shows all upcoming blocks. Click any block to remove it. Removing a block makes those slots available again immediately.',
        type: 'admin',
      },
    ],
  },
  {
    version: 'Cron Jobs',
    date: 'May 2026',
    tag: 'minor',
    dot: '✦',
    cards: [
      {
        emoji: '🌅',
        title: 'Morning reminder — runs at 7am Mon–Fri',
        desc: 'Every weekday morning, all customers with appointments today receive a reminder email automatically. No action needed from you.',
        type: 'auto',
      },
      {
        emoji: '📊',
        title: 'Owner daily summary email',
        desc: 'At 7am you also receive a daily summary email listing all of today\'s appointments — names, times, services — so you can plan your day at a glance.',
        type: 'auto',
      },
      {
        emoji: '🌙',
        title: 'Evening sweep — runs at 6pm Mon–Fri',
        desc: 'At end of day, any appointments still showing as Upcoming (customer didn\'t show, you didn\'t update status) are automatically pushed to Missing.',
        type: 'auto',
      },
      {
        emoji: '📅',
        title: 'Tomorrow reminders',
        desc: 'The evening sweep also sends reminder emails to all customers with appointments tomorrow. They get a heads-up the night before.',
        type: 'auto',
      },
    ],
  },
  {
    version: 'Multi-owner Emails',
    date: 'May 2026',
    tag: 'minor',
    dot: '✦',
    cards: [
      {
        emoji: '👥',
        title: 'Two owners receive all notifications',
        desc: 'Both owner email addresses receive every notification — new bookings, new messages, customer reschedules, customer cancellations, and daily summaries.',
        type: 'auto',
      },
      {
        emoji: '↩️',
        title: 'Reply-to goes to first owner',
        desc: 'When a customer replies to any Bella 7 email, the reply goes to the primary owner email (the first address in the list).',
        type: 'auto',
      },
    ],
  },
];

const FIXES = [
  {
    emoji: '🔌',
    title: 'Socket session sharing',
    desc: 'Socket.IO and Express were using two different session instances — admin auth on socket always failed. Fixed by extracting session into a single shared middleware.',
    date: 'May 2026',
  },
  {
    emoji: '📦',
    title: 'TIME_SLOTS missing from client constants',
    desc: 'BlockModal imported TIME_SLOTS from client constants which no longer exported it. Fixed with inline slot generation.',
    date: 'May 2026',
  },
  {
    emoji: '🏗️',
    title: 'Railway build failing on ESLint errors',
    desc: 'Five ESLint errors blocked production build — unused imports, anonymous default export, useless escape. All resolved before deploy.',
    date: 'May 2026',
  },
  {
    emoji: '📐',
    title: 'Booking card overflowing on desktop',
    desc: 'DatePills component was pushing the booking card wider than its container. Fixed with min-width: 0 and overflow: hidden on the card.',
    date: 'May 2026',
  },
  {
    emoji: '📱',
    title: 'Hero animation stutter on mobile',
    desc: 'CSS blur filter animations on blob elements caused severe jank on mobile GPUs. Blobs hidden entirely on mobile via media query.',
    date: 'May 2026',
  },
  {
    emoji: '🔁',
    title: 'Tab remounting on every switch',
    desc: 'AppointmentsTable was keyed with tab name — remounted and re-fetched on every tab switch. Fixed by keeping all tabs mounted and hiding with display: none.',
    date: 'May 2026',
  },
  {
    emoji: '🔒',
    title: 'homepage breaking asset paths in production',
    desc: 'CRA homepage set to "." caused JS/CSS to load from relative paths — broke on sub-routes. Fixed by setting homepage to "/".',
    date: 'May 2026',
  },
  {
    emoji: '↻',
    title: 'Reschedule blocked on missing and cancelled',
    desc: 'Server was rejecting reschedule attempts for missing and cancelled appointments. Fixed the status guard and added auto-restore to upcoming.',
    date: 'May 2026',
  },
  {
    emoji: '🔊',
    title: 'Alert chime blocked by browser',
    desc: 'Chrome blocks audio before user gesture. Fixed by unlocking AudioContext on first bell click with a silent buffer play.',
    date: 'May 2026',
  },
  {
    emoji: '📧',
    title: 'replyTo breaking customer emails',
    desc: 'After adding multi-owner support, replyTo was receiving an array instead of a single string — Brevo rejected it. Fixed by using getOwnerEmails()[0] for replyTo everywhere.',
    date: 'May 2026',
  },
];

const TYPE_STYLES = {
  admin:    { label: 'Admin',    cls: 'wn__type--admin' },
  customer: { label: 'Customer', cls: 'wn__type--customer' },
  auto:     { label: 'Auto',     cls: 'wn__type--auto' },
};

const TAG_STYLES = {
  major: { label: 'Major', cls: 'wn__tag--major' },
  minor: { label: 'Minor', cls: 'wn__tag--minor' },
  patch: { label: 'Patch', cls: 'wn__tag--patch' },
};

export default function WhatsNew() {
  const [activeTab, setActiveTab] = useState('features');

  return (
    <div className="wn">
      <header className="wn__header">
        <span className="mono wn__kicker">Owner guide</span>
        <h1 className="display wn__heading">How it <em>works</em>.</h1>
        <p className="wn__sub">Everything Bella 7 does — and how to use it.</p>
      </header>

      <div className="wn__tabs">
        <button
          type="button"
          className={`wn__tab ${activeTab === 'features' ? 'wn__tab--active' : ''}`}
          onClick={() => setActiveTab('features')}
        >
          Features
        </button>
        <button
          type="button"
          className={`wn__tab ${activeTab === 'fixes' ? 'wn__tab--active' : ''}`}
          onClick={() => setActiveTab('fixes')}
        >
          Fixes
        </button>
      </div>

      {activeTab === 'features' && (
        <div className="wn__timeline">
          {FEATURES.map((release, ri) => (
            <div key={ri} className="wn__release">
              <div className={`wn__dot ${ri > 0 ? 'wn__dot--older' : ''}`}>
                {release.dot}
              </div>
              <div className="wn__release-content">
                <div className="wn__release-header">
                  <span className="wn__release-name display">{release.version}</span>
                  <span className="wn__release-date mono">{release.date}</span>
                  <span className={`wn__tag ${TAG_STYLES[release.tag].cls}`}>
                    {TAG_STYLES[release.tag].label}
                  </span>
                </div>
                <div className="wn__cards">
                  {release.cards.map((card, ci) => (
                    <div key={ci} className="wn__card">
                      <div className="wn__card-top">
                        <span className="wn__card-emoji">{card.emoji}</span>
                        <div className="wn__card-info">
                          <div className="wn__card-title">{card.title}</div>
                          <div className="wn__card-desc">{card.desc}</div>
                        </div>
                        <span className={`wn__type ${TYPE_STYLES[card.type].cls}`}>
                          {TYPE_STYLES[card.type].label}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'fixes' && (
        <div className="wn__fixes">
          {FIXES.map((fix, i) => (
            <div key={i} className="wn__fix-row">
              <span className="wn__fix-icon">{fix.emoji}</span>
              <div className="wn__fix-content">
                <div className="wn__fix-title">{fix.title}</div>
                <div className="wn__fix-desc">{fix.desc}</div>
              </div>
              <span className="wn__fix-date mono">{fix.date}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}